import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { isRouteAccessible, UserRole } from '../../utils/rbac';
import toast from 'react-hot-toast';

interface ProtectedRouteProps {
    children: React.ReactNode;
    requiredRole?: UserRole;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
    children,
    requiredRole
}) => {
    const { token, user } = useSelector((state: RootState) => state.auth);
    const location = useLocation();

    // Check if user is authenticated
    if (!token || !user) {
        return <Navigate to="/" replace />;
    }

    // Check role-based access
    if (requiredRole && user.role !== requiredRole) {
        toast.error('You do not have permission to access this page');
        if (user.role === 'DEALER') {
            return <Navigate to="/dealer/dashboard" replace />;
        } else if (user.role === 'USER') {
            return <Navigate to="/user/profile" replace />;
        }
        return <Navigate to="/admin" replace />;
    }

    // For dealer routes, skip RBAC check (dealers have their own route structure)
    if (user.role === 'DEALER' && location.pathname.startsWith('/dealer')) {
        return <>{children}</>;
    }

    // Check route-specific permissions for admin routes
    if (user.role === 'admin' || user.role === 'manager' || user.role === 'exclusive') {
        const hasAccess = isRouteAccessible(user.role as UserRole, location.pathname);
        console.log('🛡️ ProtectedRoute Permission check:', {
            userRole: user.role,
            pathname: location.pathname,
            hasAccess,
            userEmail: user.email
        });
        
        if (!hasAccess) {
            console.log('❌ Access denied for:', { userRole: user.role, pathname: location.pathname });
            toast.error('You do not have permission to access this page');
            return <Navigate to="/admin" replace />;
        }
        
        console.log('✅ Access granted for:', { userRole: user.role, pathname: location.pathname });
    }

    return <>{children}</>;
};

export default ProtectedRoute; 